import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { full_name: "", email: "", dob: "", address: "", package_id: "", payment_status: "pending" };

export default function Travelers() {
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [travelerRes, packageRes] = await Promise.all([api.get("/travelers"), api.get("/packages")]);
      setItems(travelerRes.data);
      setPackages(packageRes.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setForm(empty);
    setEditingId(null);
    setOpen(true);
  };
  const openEdit = (t) => {
    setForm({
      full_name: t.full_name,
      email: t.email,
      dob: t.dob,
      address: t.address,
      package_id: t.package_id || "",
      payment_status: t.payment_status || "pending",
    });
    setEditingId(t.id);
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/travelers/${editingId}`, form);
        toast.success("Traveler updated");
      } else {
        await api.post("/travelers", form);
        toast.success("Traveler added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this traveler?")) return;
    try {
      await api.delete(`/travelers/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="travelers-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">CRM</div>
          <h1 className="font-display text-4xl mt-2 text-[#0F172A]">Travelers</h1>
          <p className="text-[#64748B] mt-2 text-sm">Manage everyone in your travel network.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="bg-[#1E3A8A] hover:bg-[#172A6B] text-white" data-testid="add-traveler-btn">
              <Plus size={16} className="mr-2" /> Add traveler
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{editingId ? "Edit traveler" : "Add traveler"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Full name</Label>
                <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="traveler-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="traveler-email-input" />
              </div>
              <div className="space-y-2">
                <Label>Date of birth</Label>
                <Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} data-testid="traveler-dob-input" />
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="traveler-address-input" />
              </div>
              <div className="space-y-2">
                <Label>Assigned package</Label>
                <select
                  className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm"
                  value={form.package_id}
                  onChange={(e) => setForm({ ...form, package_id: e.target.value })}
                  data-testid="traveler-package-select"
                >
                  <option value="">Select package</option>
                  {packages.map((pkg) => (
                    <option key={pkg.id} value={pkg.id}>
                      {pkg.title} — ₹{Number(pkg.price).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Payment status</Label>
                <select
                  className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm"
                  value={form.payment_status}
                  onChange={(e) => setForm({ ...form, payment_status: e.target.value })}
                  data-testid="traveler-payment-status-select"
                >
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="traveler-cancel-btn">Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#1E3A8A] hover:bg-[#172A6B] text-white" data-testid="traveler-save-btn">
                {saving ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-2xl card-soft overflow-hidden" data-testid="travelers-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Full name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Date of birth</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Package</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-[#64748B]">No travelers yet.</TableCell>
              </TableRow>
            ) : (
              items.map((t) => (
                <TableRow key={t.id} data-testid={`traveler-row-${t.id}`}>
                  <TableCell className="font-medium">{t.full_name}</TableCell>
                  <TableCell>{t.email}</TableCell>
                  <TableCell>{t.dob}</TableCell>
                  <TableCell className="max-w-xs truncate text-[#64748B]">{t.address}</TableCell>
                  <TableCell>{packages.find((pkg) => pkg.id === t.package_id)?.title || "-"}</TableCell>
                  <TableCell>
                    <span className={`rounded-full px-2 py-1 text-xs ${
                      t.payment_status === "paid"
                        ? "bg-[#ECFDF5] text-[#166534]"
                        : t.payment_status === "cancelled"
                        ? "bg-[#FEE2E2] text-[#991B1B]"
                        : "bg-[#F8FAFC] text-[#475569]"
                    }`}>
                      {t.payment_status || "pending"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`edit-traveler-${t.id}`}>
                      <Pencil size={14} />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => del(t.id)} data-testid={`delete-traveler-${t.id}`}>
                      <Trash2 size={14} className="text-[#DC2626]" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
